import {
  marginLabKey,
  marginMatKey,
  type BidPayload,
  type LiveMargins,
  type LiveModelOverrides,
} from './bidDisplay';

export function communityOverviewDirty(
  community: Record<string, unknown>,
  fields: {
    name: string;
    numLots: string;
    city: string;
    zip: string;
    sageId: string;
    scopeIds: number[];
    quoteFooterHtml: string;
  },
  bidLocked?: boolean
): boolean {
  if (bidLocked) return false;
  const savedScopes = Array.isArray(community.scopes)
    ? (community.scopes as { id?: unknown }[])
        .map((s) => Number(s.id))
        .filter((id) => id > 0)
        .sort((a, b) => a - b)
    : [];
  const draftScopes = [...fields.scopeIds].sort((a, b) => a - b);
  return (
    fields.name.trim() !== String(community.name ?? '').trim() ||
    fields.numLots !== String(community.num_lots ?? '') ||
    fields.city !== String(community.city ?? '') ||
    fields.zip !== String(community.zip ?? '') ||
    fields.sageId.trim() !== String(community.sage_id ?? '').trim() ||
    savedScopes.join(',') !== draftScopes.join(',') ||
    normalizeQuoteFooterHtml(fields.quoteFooterHtml) !==
      normalizeQuoteFooterHtml(String(community.quote_footer_html ?? ''))
  );
}

/** Normalize TipTap / server HTML so cosmetic differences do not count as dirty. */
export function normalizeQuoteFooterHtml(html: string): string {
  return String(html ?? '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    // TipTap wraps list text in <p>; server/default often does not.
    .replace(/<li[^>]*>\s*<p[^>]*>([\s\S]*?)<\/p>\s*<\/li>/gi, '<li>$1</li>')
    .replace(/<p[^>]*>\s*<\/p>/gi, '')
    // Drop whitespace between tags (DB has newlines; TipTap does not).
    .replace(/>\s+</g, '><')
    .replace(/\s+/g, ' ')
    .trim();
}

export function suppliersDraftDirty(
  saved: Record<string, string>,
  draft: Record<string, string>,
  bidLocked?: boolean
): boolean {
  if (bidLocked) return false;
  return JSON.stringify(saved) !== JSON.stringify(draft);
}

export function catalogModelFormDirty(
  model: Record<string, unknown>,
  fields: {
    name: string;
    elevation: string;
    optionName: string;
    modelCode: string;
    productTypeId: string;
    floorTypeId: string;
    series: string;
    notes: string;
  }
): boolean {
  return (
    fields.name.trim() !== String(model.name ?? '').trim() ||
    fields.elevation.trim() !== String(model.elevation ?? '').trim() ||
    fields.optionName.trim() !== String(model.option_name ?? '').trim() ||
    fields.modelCode.trim() !== String(model.model_code ?? '').trim() ||
    fields.productTypeId !== (model.product_type_id != null ? String(model.product_type_id) : '') ||
    fields.floorTypeId !== (model.floor_type_id != null ? String(model.floor_type_id) : '') ||
    fields.series.trim() !== String(model.series ?? '').trim() ||
    fields.notes.trim() !== String(model.notes ?? '').trim()
  );
}

function modelOverridesDirty(bid: BidPayload, overrides: LiveModelOverrides): boolean {
  for (const m of bid.models) {
    const live = overrides[m.id];
    if (!live) continue;
    const stored = m.overrides ?? {};

    for (const def of bid.phaseDefinitions ?? []) {
      const key = String(def.id);
      for (const marginKey of [marginMatKey(key), marginLabKey(key)]) {
        const raw = live[marginKey];
        if (typeof raw !== 'string') continue;
        const pending = raw === '' ? null : Number.parseFloat(raw);
        const current = stored[marginKey] != null ? Number(stored[marginKey]) : null;
        if (pending !== current) return true;
      }
    }

    const lockedRaw = live.lockedPrice;
    if (typeof lockedRaw === 'string') {
      const pending = lockedRaw === '' ? null : Number.parseFloat(lockedRaw);
      const current = stored.lockedPrice != null ? Number(stored.lockedPrice) : null;
      if (pending !== current) return true;
    }

    if (live.phaseQtyPrices) {
      for (const [phaseKey, raw] of Object.entries(live.phaseQtyPrices)) {
        const pending = raw === '' || raw == null ? null : Number.parseFloat(raw);
        const current =
          m.phaseQtyPrices?.[phaseKey] != null ? Number(m.phaseQtyPrices[phaseKey]) : null;
        const pendingNorm = pending != null && Number.isFinite(pending) ? pending : null;
        const currentNorm = current != null && Number.isFinite(current) ? current : null;
        if (pendingNorm !== currentNorm) return true;
      }
    }
  }
  return false;
}

export function bidBuilderDirty(
  bid: BidPayload | undefined,
  live: LiveMargins,
  overrides: LiveModelOverrides,
  opts: {
    bidLocked: boolean;
    marginsSaving: boolean;
    modelsSaving: boolean;
    revNote: string;
    revCategory?: string;
  }
): boolean {
  if (!bid || opts.bidLocked) return false;
  if (opts.marginsSaving || opts.modelsSaving) return true;
  if (opts.revNote.trim() || opts.revCategory) return true;

  if (live.salesTaxPct !== bid.salesTaxPct) return true;

  const marginKeys = new Set([
    ...Object.keys(bid.margins),
    ...Object.keys(live).filter((k) => k !== 'salesTaxPct'),
  ]);
  for (const key of marginKeys) {
    if ((live[key] ?? 0) !== (bid.margins[key] ?? 0)) return true;
  }

  return modelOverridesDirty(bid, overrides);
}
